import { useState, useEffect, useRef } from 'react';
import { X, RefreshCw, Unlock, Search, Eye, EyeOff, CheckCircle2, AlertCircle } from 'lucide-react';
import { useListParticipants, useResetParticipantPassword } from '@/hooks/useAuth';
import type { Participant } from 'shared';

interface UnlockUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/** Calculates age in full years from an ISO date string. */
function calcAge(dataNascimento: string): number {
  const birth = new Date(dataNascimento);
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

/** Generates a secure random password */
function generatePassword(length = 12): string {
  const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  const lower = 'abcdefghijklmnopqrstuvwxyz';
  const digits = '0123456789';
  const special = '!@#$%&*';
  const all = upper + lower + digits + special;

  const required = [
    upper[Math.floor(Math.random() * upper.length)],
    lower[Math.floor(Math.random() * lower.length)],
    digits[Math.floor(Math.random() * digits.length)],
    special[Math.floor(Math.random() * special.length)],
  ];

  const rest = Array.from({ length: length - required.length }, () =>
    all[Math.floor(Math.random() * all.length)]
  );

  return [...required, ...rest].sort(() => Math.random() - 0.5).join('');
}

/** Returns the status label and CSS classes for a participant's estadoRegisto. */
function statusBadge(estado: string): { label: string; cls: string } {
  switch (estado) {
    case 'Ativo':
      return { label: 'Ativo', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' };
    case 'Pendente':
      return { label: 'Pendente', cls: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400' };
    case 'Suspenso':
      return { label: 'Suspenso', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' };
    default:
      return { label: estado, cls: 'bg-muted text-muted-foreground' };
  }
}

export default function UnlockUserModal({ isOpen, onClose }: UnlockUserModalProps) {
  const [search, setSearch] = useState('');
  const [selectedParticipant, setSelectedParticipant] = useState<Participant | null>(null);
  const [newPassword, setNewPassword] = useState(() => generatePassword());
  const [showPassword, setShowPassword] = useState(false);
  const [toast, setToast] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const searchRef = useRef<HTMLInputElement>(null);
  const resetPassword = useResetParticipantPassword();
  const { data: participantsData, isLoading: participantsLoading } = useListParticipants(500);

  // Focus search on open & reset state
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchRef.current?.focus(), 50);
      setSearch('');
      setSelectedParticipant(null);
      setNewPassword(generatePassword());
      setShowPassword(false);
      setToast(null);
    }
  }, [isOpen]);

  // Auto-dismiss toast after 4 s
  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  if (!isOpen) return null;

  const q = search.toLowerCase().trim();

  const filteredParticipants = (participantsData?.participants ?? []).filter((p) => {
    if (q.length < 2) return false;
    const fullName = `${p.nome} ${p.apelido}`.toLowerCase();
    const age = calcAge(p.dataNascimento).toString();
    return (
      fullName.includes(q) ||
      (p.email ?? '').toLowerCase().includes(q) ||
      age === q ||
      (p.telemovel ?? '').includes(q)
    );
  });

  const handleReset = () => {
    if (!selectedParticipant || !newPassword.trim()) return;

    resetPassword.mutate(
      { participantId: selectedParticipant.id, data: { newPassword } },
      {
        onSuccess: () => {
          setToast({
            type: 'success',
            message: `Password de "${selectedParticipant.nome} ${selectedParticipant.apelido}" alterada com sucesso.`,
          });
          setSelectedParticipant(null);
          setSearch('');
          setNewPassword(generatePassword());
        },
        onError: (err: Error) => {
          setToast({ type: 'error', message: err.message || 'Erro ao alterar a password.' });
        },
      }
    );
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="unlock-modal-title"
    >
      <div className="bg-card border border-border rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <div className="flex items-center gap-2">
            <Unlock className="w-5 h-5 text-primary" />
            <h2 id="unlock-modal-title" className="text-lg font-semibold text-foreground">
              Desbloquear Utilizador
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
            aria-label="Fechar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5">
          {/* Toast */}
          {toast && (
            <div
              className={`flex items-center gap-2 rounded-lg px-4 py-3 text-sm font-medium ${
                toast.type === 'success'
                  ? 'bg-green-50 text-green-800 border border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800'
                  : 'bg-red-50 text-red-800 border border-red-200 dark:bg-red-900/20 dark:text-red-400 dark:border-red-800'
              }`}
            >
              {toast.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 shrink-0" />
              )}
              {toast.message}
            </div>
          )}

          {/* Search field */}
          <div>
            <label className="label mb-1.5 block" htmlFor="participant-search">
              Pesquisar participante
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <input
                ref={searchRef}
                id="participant-search"
                type="text"
                className="input pl-9"
                placeholder="Nome, email, idade ou telemóvel (mín. 2 caracteres)"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setSelectedParticipant(null);
                }}
                autoComplete="off"
              />
            </div>

            {/* Search results dropdown */}
            {q.length >= 2 && !selectedParticipant && (
              <div className="mt-1 border border-border rounded-md bg-card shadow-md max-h-56 overflow-y-auto">
                {participantsLoading ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">A carregar...</div>
                ) : filteredParticipants.length === 0 ? (
                  <div className="px-4 py-3 text-sm text-muted-foreground">Sem resultados.</div>
                ) : (
                  filteredParticipants.map((p) => {
                    const age = calcAge(p.dataNascimento);
                    const badge = statusBadge(p.estadoRegisto);
                    return (
                      <button
                        key={p.id}
                        className="w-full flex items-start gap-3 px-4 py-2.5 hover:bg-accent text-left transition-colors"
                        onClick={() => {
                          setSelectedParticipant(p);
                          setSearch(`${p.nome} ${p.apelido}`);
                        }}
                      >
                        {/* Avatar */}
                        <div className="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                          {p.nome.charAt(0).toUpperCase()}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-foreground">
                            {p.nome} {p.apelido}
                            <span className="ml-1.5 text-xs text-muted-foreground font-normal">
                              · {age} anos
                            </span>
                          </div>
                          <div className="text-xs text-muted-foreground truncate">
                            {p.email ?? <span className="italic">sem email</span>}
                            {p.telemovel && (
                              <span className="ml-2 text-muted-foreground/70">· {p.telemovel}</span>
                            )}
                          </div>
                        </div>

                        {/* Status badge */}
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium shrink-0 mt-0.5 ${badge.cls}`}>
                          {badge.label}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            )}
          </div>

          {/* Selected participant summary */}
          {selectedParticipant && (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/40 border border-border">
              <div className="w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-bold shrink-0">
                {selectedParticipant.nome.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground">
                  {selectedParticipant.nome} {selectedParticipant.apelido}
                  <span className="ml-1.5 text-xs text-muted-foreground font-normal">
                    · {calcAge(selectedParticipant.dataNascimento)} anos
                  </span>
                </p>
                <p className="text-xs text-muted-foreground truncate">
                  {selectedParticipant.email ?? 'sem email'}
                  {selectedParticipant.telemovel && ` · ${selectedParticipant.telemovel}`}
                </p>
              </div>
              <button
                type="button"
                onClick={() => { setSelectedParticipant(null); setSearch(''); }}
                className="text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Remover selecção"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* Password field — only shown when participant is selected */}
          {selectedParticipant && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="label" htmlFor="new-password">
                  Nova password
                </label>
                <button
                  type="button"
                  onClick={() => setNewPassword(generatePassword())}
                  className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors font-medium"
                >
                  <RefreshCw className="w-3 h-3" />
                  Gerar nova
                </button>
              </div>
              <div className="relative">
                <input
                  id="new-password"
                  type={showPassword ? 'text' : 'password'}
                  className="input pr-10 font-mono"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Nova password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? 'Ocultar password' : 'Mostrar password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">
                A password pode ser editada antes de confirmar. O participante terá de utilizar esta nova password na próxima sessão.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-border">
          <button onClick={onClose} className="btn-outline text-sm">
            Cancelar
          </button>
          <button
            onClick={handleReset}
            disabled={!selectedParticipant || !newPassword.trim() || resetPassword.isPending}
            className="btn-primary text-sm flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Unlock className="w-4 h-4" />
            {resetPassword.isPending ? 'A guardar...' : 'Alterar Password'}
          </button>
        </div>
      </div>
    </div>
  );
}
